import { useMutation, useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useContext } from 'react';
import { keys } from '@/api/queryKeys';
import { useServiceToken } from '@/auth/index';
import { AppRequests } from '@/context/AppRequests';

interface EnableDisableApiKeyParams {
  enabled: boolean;
  keyId: string;
}

export const useApiKeys = () => {
  const {
    fetchCurrentApiKeys,
    enableApiKey,
    disableApiKey,
    deleteApiKey: deleteApiKeyRequest,
    createApiKey: createApiKeyRequest,
  } = useContext(AppRequests).ApiKeysRequests;
  const { data: tokenData } = useServiceToken('lizano');

  const results = useQuery({
    queryKey: [keys.API_KEYS],
    queryFn: () => fetchCurrentApiKeys(tokenData?.token),
    enabled: Boolean(tokenData?.token),
  });

  const createApiKey = useMutation(
    async () => {
      if (!tokenData?.token) {
        return Promise.reject(new Error('No service token'));
      }

      return await createApiKeyRequest(tokenData?.token);
    },
    {
      onSuccess() {
        enqueueSnackbar('Created new API key', { variant: 'success' });
        results.refetch();
      },
    }
  );

  const enableDisableApiKey = useMutation(
    (params: EnableDisableApiKeyParams) => {
      if (!tokenData?.token) {
        return Promise.reject(new Error('No service token'));
      }

      if (params.enabled) {
        return disableApiKey(tokenData?.token, params.keyId);
      }
      return enableApiKey(tokenData?.token, params.keyId);
    },
    {
      onSuccess() {
        enqueueSnackbar('Updated API key status', { variant: 'success' });
        results.refetch();
      },
    }
  );

  const deleteApiKey = useMutation(
    (keyId: string) => {
      if (!tokenData?.token) {
        return Promise.reject(new Error('No service token'));
      }

      return deleteApiKeyRequest(tokenData?.token, keyId);
    },
    {
      onSuccess() {
        enqueueSnackbar('Deleted API key', { variant: 'success' });
        results.refetch();
      },
    }
  );

  return { ...results, createApiKey, enableDisableApiKey, deleteApiKey };
};
